import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Phone, Mail, User, FileText, Loader2, ArrowLeft, DollarSign, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GetOfferPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const prefilledAddress = searchParams.get("address") || "";

  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    email: "",
    property_address: prefilledAddress,
    property_condition: "",
    asking_price: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Update address if it comes from URL params
    if (prefilledAddress) {
      setFormData(prev => ({ ...prev, property_address: prefilledAddress }));
    }
  }, [prefilledAddress]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (value) => {
    setFormData((prev) => ({ ...prev, property_address: value }));
  };

  const handleConditionChange = (value) => {
    setFormData((prev) => ({ ...prev, property_condition: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/leads`, formData);
      navigate("/thank-you");
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly at 626-526-1420.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2" data-testid="form-logo-link">
              <div className="w-10 h-10 bg-[#D32F2F] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>JJ</span>
              </div>
              <span 
                className="hidden sm:block text-xl font-bold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                buyshouses
              </span>
            </a>

            {/* Phone */}
            <a 
              href="tel:626-526-1420" 
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium"
              data-testid="form-header-phone"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">626-526-1420</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back Link */}
          <a 
            href="/" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </a>

          {/* Header */}
          <div className="text-center mb-10">
            <h1 
              className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Get Your Free <span className="text-[#D32F2F]">Cash Offer</span>
            </h1>
            <p className="text-lg text-slate-600">
              Fill out the form below and we'll contact you within 24 hours with a fair, no-obligation cash offer.
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 md:p-8">
            <form onSubmit={handleSubmit} data-testid="offer-form">
              <div className="space-y-5">
                {/* Property Address - First and Highlighted with Autocomplete */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <Label htmlFor="property_address" className="text-slate-700 font-semibold text-base">
                    Property Address *
                  </Label>
                  <div className="mt-2">
                    <AddressAutocomplete
                      value={formData.property_address}
                      onChange={handleAddressChange}
                      placeholder="Start typing your address..."
                      inputClassName="h-12 border-slate-200 focus:border-[#D32F2F] focus:ring-[#D32F2F]/20 text-lg"
                      testId="input-address"
                    />
                  </div>
                </div>

                {/* Property Condition & Asking Price Row */}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="property_condition" className="text-slate-700 font-medium">
                      General Property Condition
                    </Label>
                    <div className="mt-1.5">
                      <Select
                        value={formData.property_condition}
                        onValueChange={handleConditionChange}
                      >
                        <SelectTrigger 
                          className="h-12 border-slate-200 focus:border-[#D32F2F] focus:ring-[#D32F2F]/20"
                          data-testid="select-condition"
                        >
                          <Home className="w-5 h-5 text-slate-400 mr-2" />
                          <SelectValue placeholder="Select condition" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Excellent">Excellent</SelectItem>
                          <SelectItem value="Very Good">Very Good</SelectItem>
                          <SelectItem value="Good">Good</SelectItem>
                          <SelectItem value="Fair">Fair</SelectItem>
                          <SelectItem value="Poor">Poor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="asking_price" className="text-slate-700 font-medium">
                      Asking Price
                    </Label>
                    <div className="relative mt-1.5">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="asking_price"
                        name="asking_price"
                        type="text"
                        placeholder=""
                        value={formData.asking_price}
                        onChange={handleChange}
                        className="pl-10 h-12 border-slate-200 focus:border-[#D32F2F] focus:ring-[#D32F2F]/20"
                        data-testid="input-asking-price"
                      />
                    </div>
                  </div>
                </div>

                {/* Full Name */}
                <div>
                  <Label htmlFor="full_name" className="text-slate-700 font-medium">
                    Full Name *
                  </Label>
                  <div className="relative mt-1.5">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="full_name"
                      name="full_name"
                      type="text"
                      placeholder="John Smith"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className="pl-10 h-12 border-slate-200 focus:border-[#D32F2F] focus:ring-[#D32F2F]/20"
                      data-testid="input-full-name"
                    />
                  </div>
                </div>

                {/* Phone & Email Row */}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 font-medium">
                      Phone *
                    </Label>
                    <div className="relative mt-1.5">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="pl-10 h-12 border-slate-200 focus:border-[#D32F2F] focus:ring-[#D32F2F]/20"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-700 font-medium">
                      Email *
                    </Label>
                    <div className="relative mt-1.5">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="pl-10 h-12 border-slate-200 focus:border-[#D32F2F] focus:ring-[#D32F2F]/20"
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="notes" className="text-slate-700 font-medium">
                    Additional Notes (Optional)
                  </Label>
                  <div className="relative mt-1.5">
                    <FileText className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                    <Textarea
                      id="notes"
                      name="notes"
                      placeholder="Tell us about your property or situation..."
                      value={formData.notes}
                      onChange={handleChange}
                      rows={4}
                      className="pl-10 border-slate-200 focus:border-[#D32F2F] focus:ring-[#D32F2F]/20 resize-none"
                      data-testid="input-notes"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all btn-primary"
                  data-testid="submit-form-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Get My Cash Offer
                      <Send className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  By submitting, you agree to our{" "}
                  <a href="#" className="underline hover:text-slate-700">Privacy Policy</a>.
                  We respect your privacy and will never share your information.
                </p>
              </div>
            </form>
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              No Obligation
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              100% Free
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Fast Response
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default GetOfferPage;
